import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Stethoscope,
  Microscope,
  GraduationCap,
  Building2,
  Rocket,
  Pill,
  Dna,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import { FadeIn, MaskedLines, Eyebrow } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";

const LAB_IMG =
  "https://images.unsplash.com/photo-1582719471384-894fbb16e074?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxzY2llbnRpZmljJTIwbGFib3JhdG9yeSUyMHJlc2VhcmNoZXIlMjBiaW9sb2d5JTIwaGVhbHRoY2FyZSUyMG1pY3Jvc2NvcGV8ZW58MHx8fHwxNzg4NjY4ODU0fDA&ixlib=rb-4.1.0&q=85";

const stakeholders = [
  { icon: Stethoscope, label: "Clinicians", copy: "Case reports, clinical reviews, and publication-ready evidence." },
  { icon: Microscope, label: "Researchers", copy: "From hypothesis to high-impact journal acceptance." },
  { icon: GraduationCap, label: "Universities", copy: "Thesis, synopsis, and institutional research support." },
  { icon: Building2, label: "CROs", copy: "Protocols, narratives, and regulatory-grade documentation." },
  { icon: Rocket, label: "Startups", copy: "Scientific storytelling that earns grants and investor trust." },
  { icon: Pill, label: "Pharma Companies", copy: "R&D consulting across discovery and development." },
  { icon: Dna, label: "Biotech Companies", copy: "Mechanistic insight and credible communication at pace." },
];

const lifecycle = [
  { n: "01", title: "Discovery", copy: "Drug discovery support, SAR / MoA analysis, and landscape intelligence." },
  { n: "02", title: "Preclinical", copy: "Study design, literature surveillance, and technical documentation." },
  { n: "03", title: "Clinical", copy: "Protocols, meta-analysis, biostatistics, and real-world evidence." },
  { n: "04", title: "Regulatory", copy: "Compliant documentation aligned with regulatory expectations." },
  { n: "05", title: "Publication", copy: "Manuscripts, journal strategy, and reviewer responses that land." },
];

export default function About() {
  useTitle("About Us — THREENNOVATORS LLP");
  const bandRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: bandRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <div data-testid="about-page">
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8 lg:pb-24">
        <FadeIn y={12}>
          <Eyebrow>About Us</Eyebrow>
        </FadeIn>
        <h1
          className="mt-6 max-w-4xl font-serif text-4xl leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          data-testid="about-headline"
        >
          <MaskedLines
            lines={[
              "At the intersection of",
              <>
                <span className="italic text-coral-500">science, medicine</span>
              </>,
              "& communication.",
            ]}
          />
        </h1>
        <FadeIn delay={0.5}>
          <p
            className="mt-8 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="about-intro"
          >
            THREENNOVATORS LLP works at the intersection of science, medicine,
            and communication — supporting clinicians, researchers,
            universities, CROs, startups, pharmaceutical companies, and
            biotechnology companies throughout the research and development
            lifecycle.
          </p>
        </FadeIn>
      </section>

      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <FadeIn>
            <Eyebrow>Who We Support</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-serif text-2xl leading-tight tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              One partner across the{" "}
              <span className="italic text-coral-500">entire ecosystem.</span>
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-stakeholder-matrix">
            {stakeholders.map((s, i) => (
              <FadeIn
                key={s.label}
                delay={i * 0.06}
                className={`group rounded-3xl border border-slate-200 bg-[#FAFAFA] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-coral-300 hover:bg-white ${
                  i === 6 ? "lg:col-start-2" : ""
                }`}
              >
                <s.icon className="h-6 w-6 text-coral-500" strokeWidth={1.6} />
                <h3 className="mt-5 font-serif text-lg text-slate-900">{s.label}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.copy}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
        <FadeIn>
          <Eyebrow>The R&amp;D Lifecycle</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-serif text-2xl leading-tight tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Present at every stage that{" "}
            <span className="italic text-coral-500">matters.</span>
          </h2>
        </FadeIn>
        <div className="mt-14 grid gap-10 lg:grid-cols-5 lg:gap-6" data-testid="about-lifecycle-roadmap">
          {lifecycle.map((step, i) => (
            <FadeIn key={step.n} delay={i * 0.08} className="relative">
              <div className="flex items-center gap-4 lg:block">
                <span className="font-serif text-4xl text-coral-400 sm:text-5xl">
                  {step.n}
                </span>
                <span
                  className="hidden h-px w-full bg-slate-200 lg:mt-5 lg:block"
                  aria-hidden="true"
                />
              </div>
              <h3 className="mt-2 font-sans text-lg font-semibold text-slate-900 lg:mt-6">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.copy}</p>
            </FadeIn>
          ))}
        </div>
      </section>

      <section ref={bandRef} className="relative overflow-hidden">
        <motion.img
          style={{ y }}
          src={LAB_IMG}
          alt="Researcher conducting biomedical analysis in a laboratory"
          className="h-[22rem] w-full scale-[1.2] object-cover sm:h-[28rem]"
        />
        <div className="absolute inset-0 bg-slate-950/45" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p
                className="max-w-2xl font-serif text-2xl leading-snug text-white sm:text-3xl lg:text-4xl"
                data-testid="about-image-quote"
              >
                “Every dataset has a story.{" "}
                <span className="italic text-coral-300">
                  We make sure it is told credibly.
                </span>
                ”
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-slate-950">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-28 lg:grid-cols-12 lg:px-8">
          <FadeIn className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-coral-400">
              Our Vision
            </p>
            <p
              className="mt-6 font-serif text-2xl leading-snug text-white sm:text-3xl"
              data-testid="about-vision-statement"
            >
              To be a trusted knowledge and innovation partner — bridging
              science, medicine, and real-world impact through high-quality
              scientific communication.
            </p>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-5">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
              <ShieldCheck className="h-8 w-8 text-coral-400" strokeWidth={1.5} />
              <h3 className="mt-5 font-serif text-xl text-white">
                100% Confidentiality
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Your data, drafts, and intellectual property remain yours —
                protected by strict confidentiality at every step of the
                engagement.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <FadeIn className="flex flex-col items-start justify-between gap-8 rounded-[2.5rem] border border-slate-200 bg-white p-10 sm:p-14 lg:flex-row lg:items-center">
          <div>
            <h2 className="font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">
              See what we can do together.
            </h2>
            <p className="mt-3 max-w-lg text-base text-slate-600">
              Explore the six service domains, or tell us about your project
              directly.
            </p>
          </div>
          <Link
            to="/services"
            data-testid="about-cta-explore-services"
            className="group inline-flex items-center gap-2 rounded-full bg-coral-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-coral-600"
          >
            Explore Our Services
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeIn>
      </section>
    </div>
  );
}
